import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, ShieldX, Search, CheckCircle2, XCircle,
  AlertTriangle, Clock, Camera, ScanLine, RotateCw,
} from "lucide-react";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";

const QC_LABELS = { PENDING: "Bekliyor", PASSED: "Onaylandı", FAILED: "Reddedildi" };

export default function QualityControlPage() {
  const [barcode, setBarcode] = useState("");
  const [piece, setPiece] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("PENDING");
  const [scannerOpen, setScannerOpen] = useState(false);
  const inputRef = useRef(null);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/qc/stats");
      setStats(data);
    } catch (e) { /* silent */ }
  };
  const loadReasons = async () => {
    try {
      const { data } = await api.get("/qc/defect-reasons");
      setReasons(data);
    } catch (e) { /* silent */ }
  };
  const loadList = async () => {
    try {
      const { data } = await api.get(`/qc/pieces?qc_filter=${filter}`);
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadStats(); loadReasons(); }, []);
  useEffect(() => { loadList(); }, [filter]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const lookup = async (bc) => {
    const code = (bc || barcode).trim();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/pieces/by-barcode/${encodeURIComponent(code)}`);
      setPiece(data);
      setSelectedReason("");
      setNotes("");
    } catch (e) {
      setPiece(null);
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const decide = async (decision) => {
    if (!piece) return;
    if (decision === "FAILED" && !selectedReason) {
      toast.error("Lütfen bir kusur sebebi seçin");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/qc/decision", {
        barcode: piece.barcode,
        decision,
        defect_reason: decision === "FAILED" ? selectedReason : "",
        notes,
      });
      toast.success(
        decision === "PASSED"
          ? `Onaylandı: ${data.barcode}`
          : `Reddedildi: ${data.barcode} → Üretime geri gönderildi`
      );
      setPiece(null);
      setBarcode("");
      setSelectedReason("");
      setNotes("");
      inputRef.current?.focus();
      loadStats();
      loadList();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const pct = useMemo(() => {
    if (!stats || stats.total === 0) return { p: 0, f: 0, pd: 0 };
    return {
      p: Math.round((stats.passed / stats.total) * 100),
      f: Math.round((stats.failed / stats.total) * 100),
      pd: Math.round((stats.pending / stats.total) * 100),
    };
  }, [stats]);

  return (
    <div>
      <PageHeader
        title="Kalite Kontrol"
        subtitle="Barkod tarayın, parçayı onaylayın veya kusur sebebiyle reddedin."
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard testid="qc-stat-total" label="Toplam Parça" value={stats?.total ?? "—"} tone="slate" icon={ShieldCheck} />
        <StatCard testid="qc-stat-passed" label={`Onaylandı (${pct.p}%)`} value={stats?.passed ?? "—"} tone="emerald" icon={CheckCircle2} />
        <StatCard testid="qc-stat-failed" label={`Reddedildi (${pct.f}%)`} value={stats?.failed ?? "—"} tone="rose" icon={XCircle} />
        <StatCard testid="qc-stat-pending" label={`Bekliyor (${pct.pd}%)`} value={stats?.pending ?? "—"} tone="amber" icon={Clock} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Left: Scan panel */}
        <div className="lg:col-span-1 card-industrial p-5">
          <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2"><ScanLine size={20}/> Parça Tara</h2>
          <div className="flex gap-2 mb-3">
            <input
              ref={inputRef}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="Barkodu okutun veya yazın..."
              data-testid="qc-barcode-input"
              className="flex-1 px-3 py-3 font-mono border-2 border-slate-300 focus:border-blue-700 outline-none"
            />
            <button onClick={() => lookup()} disabled={busy} data-testid="qc-lookup-btn" className="px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold text-sm uppercase">
              <Search size={18}/>
            </button>
            <button onClick={() => setScannerOpen(true)} data-testid="qc-scan-camera-btn" className="px-4 py-3 border-2 border-slate-800 hover:bg-slate-900 hover:text-white">
              <Camera size={18}/>
            </button>
          </div>

          {piece ? (
            <div className="border-2 border-slate-800 bg-slate-50 p-4 mt-4" data-testid="qc-piece-card">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Parça</div>
              <div className="font-display font-black text-lg text-slate-900 mt-1">{piece.category} · {piece.model}</div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <Field label="Sipariş" value={piece.order_number} mono />
                <Field label="Müşteri" value={piece.customer_name} />
                <Field label="Ölçü" value={piece.size} mono />
                <Field label="Durum" value={piece.status} mono />
                <Field label="Parça" value={`${piece.piece_number}/${piece.piece_total}`} mono />
                <Field label="Barkod" value={piece.barcode} mono />
              </div>

              <div className="mt-4">
                <label className="block text-xs uppercase font-bold text-slate-600 mb-1">Kusur Sebebi (Reddedersen zorunlu)</label>
                <select
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  data-testid="qc-reason-select"
                  className="w-full px-3 py-2.5 border-2 border-slate-300 focus:border-blue-700 outline-none"
                >
                  <option value="">— Seçiniz —</option>
                  {reasons.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                </select>
              </div>

              <div className="mt-3">
                <label className="block text-xs uppercase font-bold text-slate-600 mb-1">Not (opsiyonel)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="qc-notes-input"
                  rows={2}
                  className="w-full px-3 py-2 border-2 border-slate-300 focus:border-blue-700 outline-none text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4">
                <button onClick={() => decide("PASSED")} disabled={busy} data-testid="qc-pass-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm">
                  <CheckCircle2 size={18}/> Onayla
                </button>
                <button onClick={() => decide("FAILED")} disabled={busy} data-testid="qc-fail-btn"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold uppercase text-sm">
                  <XCircle size={18}/> Reddet
                </button>
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-start gap-1">
                <AlertTriangle size={12} className="mt-0.5"/>
                Reddedilen parçalar otomatik olarak <b>Üretimde</b> durumuna geri döner (yeniden işlem).
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-300 p-6 text-center text-slate-500 text-sm mt-4">
              Bir barkod okutarak başlayın.
            </div>
          )}
        </div>

        {/* Right: Top defects + list */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-industrial p-5">
            <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2"><ShieldAlert size={20}/> Kusur Sebepleri (En Sık)</h2>
            {(!stats?.top_defects || stats.top_defects.length === 0) ? (
              <div className="text-sm text-slate-500 py-4 text-center">Henüz reddedilen parça yok.</div>
            ) : (
              <div className="space-y-2" data-testid="qc-top-defects">
                {stats.top_defects.map((d, i) => {
                  const max = stats.top_defects[0].count || 1;
                  const w = Math.max(6, Math.round((d.count / max) * 100));
                  return (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-40 truncate text-slate-700 font-semibold" title={d.reason}>{d.reason}</div>
                      <div className="flex-1 bg-slate-100 h-4">
                        <div className="bg-rose-500 h-4" style={{ width: `${w}%` }} />
                      </div>
                      <div className="w-10 text-right tabular-nums font-bold">{d.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card-industrial p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-black text-xl flex items-center gap-2"><ShieldCheck size={20}/> QC Kayıtları</h2>
              <div className="flex gap-1 text-xs">
                {["PENDING", "PASSED", "FAILED"].map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`qc-filter-${f.toLowerCase()}`}
                    className={`px-3 py-1.5 border-2 font-bold uppercase ${filter === f ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-800"}`}
                  >
                    {QC_LABELS[f]}
                  </button>
                ))}
                <button onClick={loadList} data-testid="qc-refresh-btn" className="px-3 py-1.5 border-2 border-slate-300 hover:border-slate-800"><RotateCw size={12}/></button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="qc-rows-table">
                <thead>
                  <tr className="text-xs uppercase text-slate-600 border-b border-slate-300">
                    <th className="text-left px-2 py-2">Barkod</th>
                    <th className="text-left px-2 py-2">Sipariş</th>
                    <th className="text-left px-2 py-2">Ürün</th>
                    <th className="text-left px-2 py-2">Durum</th>
                    <th className="text-left px-2 py-2">Kusur / Not</th>
                    <th className="text-left px-2 py-2">Zaman</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Kayıt yok.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-200" data-testid={`qc-row-${r.barcode}`}>
                      <td className="px-2 py-2 font-mono text-xs">{r.barcode}</td>
                      <td className="px-2 py-2 font-mono font-bold">{r.order_number}</td>
                      <td className="px-2 py-2 text-xs">{r.category} · {r.model} · <span className="font-mono">{r.size}</span></td>
                      <td className="px-2 py-2 text-xs">
                        <QcBadge s={r.qc_status}/>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-700 max-w-[280px]">
                        {r.qc_defect_reason && <div className="font-semibold text-rose-700">{r.qc_defect_reason}</div>}
                        {r.qc_notes && <div className="text-slate-500 truncate" title={r.qc_notes}>{r.qc_notes}</div>}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{r.qc_at ? formatDateTR(r.qc_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {scannerOpen && (
        <BarcodeCameraScanner
          autoStart
          fullScreen
          onDetected={(code) => { setScannerOpen(false); setBarcode(code); lookup(code); }}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone, icon: Icon, testid }) {
  const tones = {
    slate: "border-slate-800 text-slate-900",
    emerald: "border-emerald-500 text-emerald-700",
    rose: "border-rose-500 text-rose-700",
    amber: "border-amber-500 text-amber-700",
  };
  return (
    <div className={`border-2 ${tones[tone]} bg-white p-4`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">{label}</div>
        {Icon && <Icon size={18} className="opacity-60"/>}
      </div>
      <div className="font-display font-black text-3xl tabular-nums mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-bold text-slate-500">{label}</div>
      <div className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function QcBadge({ s }) {
  const map = {
    PASSED: "bg-emerald-100 text-emerald-800 border-emerald-500",
    FAILED: "bg-rose-100 text-rose-800 border-rose-500",
    PENDING: "bg-amber-100 text-amber-800 border-amber-500",
  };
  return <span className={`inline-block px-2 py-0.5 border font-bold text-[10px] uppercase ${map[s] || map.PENDING}`}>{QC_LABELS[s] || s}</span>;
}
